import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Save, Headphones } from 'lucide-react'
import { useQuery } from 'react-query'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { FormField } from '../../../componentesCompartilhados/form'
import { InputDate } from '../../../componentesCompartilhados/form/InputDate'
import { SuporteFormData } from '../tipos/suporte'
import { suporteService } from '../services/suporte'
import { useCreateSuporte, useUpdateSuporte } from '../hooks/useSuporte'
import { zOptionalDate, zRequiredString } from '../../../utils/validacao'
import { useAnalistasSMN, useDesenvolvedores, usePrioridades, useSolicitantes, useStatusProjeto } from '../../../hooks/useDomainData'
import { toInputDate, normalizarParaBusca } from '../../../utils/formatUtils'
import { useToast } from '../../../contexts/NotificationContext'
import { useUsuario } from '../../../contexts/UsuarioContext'
import { FormularioPessoa } from '../../pessoas/componentes/FormularioPessoa'
import { AREAS_SUPORTE_FIXAS } from '../constantes/areasSuporte'

const normalizarTextoUnico = (valor?: string | null): string => {
  return (valor ?? '').replace(/\s+/g, '').toLowerCase()
}

const dataNaoFutura = (valor: string): boolean => {
  if (!valor)
  {
    return true
  }

  const [ano, mes, dia] = valor.split('-').map(Number)
  if (!ano || !mes || !dia)
  {
    return true
  }

  const dataInformada = new Date(ano, mes - 1, dia)
  const hoje = new Date()
  const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
  return dataInformada <= hojeSemHora
}

type SuporteValidacao = Pick<SuporteFormData, 'id' | 'nome' | 'prefClockify'>

const zOptionalCode = z.preprocess(
  (val) => (val === '' || val === undefined || val === null ? '' : val),
  z.union([z.string(), z.literal('')])
)

const zOptionalId = z.preprocess(
  (val) => {
    if (val === '' || val === undefined || val === null) return undefined
    const n = Number(val)
    return isNaN(n) ? undefined : n
  },
  z.union([z.number(), z.undefined()])
)

const criarSuporteSchema = (suportesExistentes: SuporteValidacao[], suporteIdEdicao?: number) => z.object({
  numeroTicket: z.preprocess(
    (val) => val === null || val === undefined ? '' : String(val),
    z.string().optional()
  ),
  nome: zRequiredString,
  prefClockify: zRequiredString.max(50, 'A quantidade máxima de caracteres do Pref. Clockify é 50'),
  ticketUrl: z.string().optional(),
  solicitanteId: z.union([z.number(), z.undefined()]).optional(),
  solicitante: z.string().optional(),
  area: z.string().optional(),
  dataAbertura: zOptionalDate.refine((val) => val == null || (typeof val === 'string' && val.trim() === '') || dataNaoFutura(val as string), {
    message: 'Data de abertura não pode ser maior que hoje.'
  }),
  dataRecebimento: zOptionalDate.refine((val) => val == null || (typeof val === 'string' && val.trim() === '') || dataNaoFutura(val as string), {
    message: 'Data de recebimento não pode ser maior que hoje.'
  }),
  idProjetoClockify: z.string().optional(),
  dataConclusao: z.string().optional(),
  statusCodigo: zOptionalCode,
  prioridadeCodigo: zOptionalCode,
  analistaSMNId: zOptionalId.optional(),
  desenvolvedorId: z.union([
    z.number(),
    z.string().transform(val => {
      if (val === '' || val === undefined || val === null) return undefined
      const num = Number(val)
      return isNaN(num) ? undefined : num
    }),
    z.undefined()
  ]).optional(),
  solucao: z.string().optional(),
}).superRefine((data, ctx) => {
  const nomeNormalizado = normalizarTextoUnico(data.nome)
  const prefNormalizado = normalizarTextoUnico(data.prefClockify)

  if (nomeNormalizado)
  {
    const nomeDuplicado = suportesExistentes.some((suporte) => {
      if (suporteIdEdicao && suporte.id === suporteIdEdicao)
      {
        return false
      }
      return normalizarTextoUnico(suporte.nome) === nomeNormalizado
    })

    if (nomeDuplicado)
    {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nome'],
        message: 'Nome do suporte já cadastrado.'
      })
    }
  }

  if (prefNormalizado)
  {
    const prefixoDuplicado = suportesExistentes.some((suporte) => {
      if (suporteIdEdicao && suporte.id === suporteIdEdicao)
      {
        return false
      }
      return normalizarTextoUnico(suporte.prefClockify || '') === prefNormalizado
    })

    if (prefixoDuplicado)
    {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prefClockify'],
        message: 'Prefixo já cadastrado.'
      })
    }
  }
})

interface FormularioSuporteProps {
  isOpen: boolean
  onClose: () => void
  suporte?: SuporteFormData | null
  isEditing?: boolean
}

export function FormularioSuporte({ isOpen, onClose, suporte, isEditing = false }: FormularioSuporteProps) {
  const { data: status = [] } = useStatusProjeto()
  const { data: prioridades = [] } = usePrioridades()
  const { data: analistasSMN = [] } = useAnalistasSMN()
  const { data: desenvolvedores = [] } = useDesenvolvedores()
  const { data: solicitantes = [], refetch: refetchSolicitantes } = useSolicitantes()
  const statusPermitidos = new Set(['backlog', 'desenvolvimento', 'testes internos'])
  const statusCodigosExtras = new Set([8, 10, 11])
  const statusFiltrados = status.filter((item: any) => {
    const codigo = item.codigo ?? item.Codigo
    const descricao = (item.descricao ?? item.Descricao ?? '').toString().trim().toLowerCase()
    return statusPermitidos.has(descricao) || (codigo != null && statusCodigosExtras.has(Number(codigo)))
  })
  const { usuario } = useUsuario()
  const toast = useToast()

  const isAdmin = usuario?.papeis?.includes('1') ?? false
  const isAnalista = !isAdmin && (usuario?.papeis?.includes('2') ?? false)
  const isDesenvolvedor = !isAdmin && (usuario?.papeis?.includes('3') ?? false)
  const pessoaId = usuario?.pessoaId

  const { data: suportesValidacao } = useQuery(
    ['suportes-validacao-unico'],
    () => suporteService.listarSuportes({}),
    {
      enabled: isOpen,
      staleTime: 30000
    }
  )

  const suporteIdEdicao = suporte?.id ? Number(suporte.id) : undefined
  const suportesParaValidacao = (suportesValidacao || []) as SuporteValidacao[]
  const suporteSchema = useMemo(
    () => criarSuporteSchema(suportesParaValidacao, suporteIdEdicao),
    [suportesParaValidacao, suporteIdEdicao]
  )

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SuporteFormData>({
    resolver: zodResolver(suporteSchema),
    mode: 'onSubmit',
    defaultValues: {
      numeroTicket: '',
      nome: '',
      prefClockify: '',
      ticketUrl: 'https://momentum.movidesk.com/Ticket/Edit/',
      idProjetoClockify: '',
      solicitanteId: undefined,
      solicitante: '',
      area: '',
      dataAbertura: '',
      dataRecebimento: '',
      dataConclusao: '',
      statusCodigo: '',
      prioridadeCodigo: '',
      analistaSMNId: isAnalista && pessoaId ? String(pessoaId) : '',
      desenvolvedorId: isDesenvolvedor && pessoaId ? String(pessoaId) : '',
      solucao: '',
    },
  })

  const createMutation = useCreateSuporte()
  const updateMutation = useUpdateSuporte()

  const suporteAtual = useMemo(() => suporte || null, [suporte])
  const lastSuporteId = useRef<number | null>(null)
  const lastResetSuporteRef = useRef<SuporteFormData | null>(null)
  const resetAfterLoad = useRef<boolean>(false)

  const numeroTicket = watch('numeroTicket')
  const solicitanteAtual = watch('solicitante')

  const [isSolicitanteDropdownOpen, setIsSolicitanteDropdownOpen] = useState(false)
  const [solicitanteSearch, setSolicitanteSearch] = useState('')
  const [isSolicitanteModalOpen, setIsSolicitanteModalOpen] = useState(false)

  const solicitantesFiltrados = useMemo(() => {
    const termoNorm = normalizarParaBusca(solicitanteSearch)
    if (!termoNorm)
    {
      return solicitantes || []
    }

    return (solicitantes || []).filter((pessoa: any) => {
      const nome = (pessoa.nome ?? pessoa.Nome ?? '').toString()
      return normalizarParaBusca(nome).includes(termoNorm)
    })
  }, [solicitantes, solicitanteSearch])

  const handleSelectSolicitante = (pessoa: any) => {
    const nome = pessoa.nome ?? pessoa.Nome ?? ''
    const idPessoa =
      pessoa.id
      ?? pessoa.idPessoa
      ?? pessoa.IdPessoa
      ?? pessoa.Id
      ?? 0

    setValue('solicitante', nome, { shouldDirty: true, shouldValidate: true, shouldTouch: true })
    setValue('solicitanteId', idPessoa ? Number(idPessoa) : undefined, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    })
    setSolicitanteSearch(nome)
    setIsSolicitanteDropdownOpen(false)
  }

  const handleOpenSolicitanteModal = () => {
    setIsSolicitanteModalOpen(true)
    setIsSolicitanteDropdownOpen(false)
  }

  const handleCloseSolicitanteModal = async () => {
    setIsSolicitanteModalOpen(false)
    await refetchSolicitantes()
  }

  useEffect(() => {
    if (numeroTicket && numeroTicket.trim() !== '') {
      const ticketUrlBase = 'https://momentum.movidesk.com/Ticket/Edit/'
      const ticketNumber = numeroTicket.replace(/[^0-9]/g, '').trim()
      if (ticketNumber !== numeroTicket) {
        setValue('numeroTicket', ticketNumber, {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false
        })
      }
      setValue('ticketUrl', `${ticketUrlBase}${ticketNumber}`, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false
      })
    }
  }, [numeroTicket, setValue])

  useEffect(() => {
    if (isOpen && isAnalista && pessoaId) {
      setValue('analistaSMNId', String(pessoaId), { shouldValidate: false })
    }
  }, [isOpen, isAnalista, pessoaId, setValue])

  useEffect(() => {
    if (isOpen && isDesenvolvedor && pessoaId) {
      setValue('desenvolvedorId', String(pessoaId), { shouldValidate: false })
    }
  }, [isOpen, isDesenvolvedor, pessoaId, setValue])

  useEffect(() => {
    if (!isOpen)
    {
      lastSuporteId.current = null
      lastResetSuporteRef.current = null
      resetAfterLoad.current = false
      return
    }

    if (suporteAtual)
    {
      const suporteIdAtual = suporteAtual.id ?? null
      const deveResetar = lastSuporteId.current !== suporteIdAtual
        || !resetAfterLoad.current
        || lastResetSuporteRef.current !== suporteAtual

      if (deveResetar)
      {
        const normalizedSuporte: SuporteFormData = {
          ...suporteAtual,
          id: suporteAtual.id,
          numeroTicket: suporteAtual.numeroTicket || '',
          nome: suporteAtual.nome || '',
          prefClockify: suporteAtual.prefClockify || '',
          ticketUrl: suporteAtual.ticketUrl || 'https://momentum.movidesk.com/Ticket/Edit/',
          idProjetoClockify: suporteAtual.idProjetoClockify || '',
          solicitanteId: suporteAtual.solicitanteId ?? undefined,
          solicitante: suporteAtual.solicitante || '',
          area: suporteAtual.area || '',
          dataAbertura: toInputDate(suporteAtual.dataAbertura) ?? '',
          dataRecebimento: toInputDate(suporteAtual.dataRecebimento) ?? '',
          dataConclusao: toInputDate(suporteAtual.dataConclusao) ?? '',
          statusCodigo: suporteAtual.statusCodigo || '',
          prioridadeCodigo: suporteAtual.prioridadeCodigo || '',
          analistaSMNId: suporteAtual.analistaSMNId || '',
          desenvolvedorId: suporteAtual.desenvolvedorId ?? '',
          solucao: suporteAtual.solucao || '',
        }

        if (isAnalista && pessoaId)
          normalizedSuporte.analistaSMNId = String(pessoaId)
        if (isDesenvolvedor && pessoaId)
          normalizedSuporte.desenvolvedorId = String(pessoaId)

        reset(normalizedSuporte)
        setSolicitanteSearch(suporteAtual.solicitante || '')
        lastSuporteId.current = suporteIdAtual
        lastResetSuporteRef.current = suporteAtual
        resetAfterLoad.current = true
      }
    }
    else
    {
      setSolicitanteSearch('')
      reset({
        numeroTicket: '',
        nome: '',
        prefClockify: '',
        ticketUrl: 'https://momentum.movidesk.com/Ticket/Edit/',
        idProjetoClockify: '',
        solicitanteId: undefined,
        solicitante: '',
        area: '',
        dataAbertura: '',
        dataRecebimento: '',
        dataConclusao: '',
        statusCodigo: '',
        prioridadeCodigo: '',
        analistaSMNId: isAnalista && pessoaId ? String(pessoaId) : '',
        desenvolvedorId: isDesenvolvedor && pessoaId ? String(pessoaId) : '',
        solucao: '',
      })
      resetAfterLoad.current = true
    }
  }, [suporteAtual, reset, isOpen, isAnalista, isDesenvolvedor, pessoaId])

  const isErroDuplicidade = (mensagem?: string) =>
    mensagem === 'Nome do suporte já cadastrado.'
    || mensagem === 'Prefixo já cadastrado.'

  const preencherValoresPadrao = (data: SuporteFormData): SuporteFormData => {
    const primeiroStatus = statusFiltrados[0] as { codigo?: number; Codigo?: number } | undefined
    const primeiroPrioridade = prioridades[0] as { codigo?: number; Codigo?: number } | undefined
    const primeiroAnalista = analistasSMN[0] as { idPessoa?: number; IdPessoa?: number } | undefined
    const codigoStatus = primeiroStatus ? (primeiroStatus.codigo ?? primeiroStatus.Codigo) : undefined
    const codigoPrioridade = primeiroPrioridade ? (primeiroPrioridade.codigo ?? primeiroPrioridade.Codigo) : undefined
    const idAnalista = primeiroAnalista ? (primeiroAnalista.idPessoa ?? primeiroAnalista.IdPessoa) : undefined
    const dataHoje = new Date().toISOString().slice(0, 10)

    return {
      ...data,
      ticketUrl: data.ticketUrl?.trim() || 'https://momentum.movidesk.com/Ticket/Edit/',
      dataRecebimento: data.dataRecebimento?.trim() || dataHoje || '',
      statusCodigo: data.statusCodigo?.trim() ? data.statusCodigo : String(codigoStatus ?? ''),
      prioridadeCodigo: data.prioridadeCodigo?.trim() ? data.prioridadeCodigo : String(codigoPrioridade ?? ''),
      analistaSMNId: (data.analistaSMNId !== undefined && data.analistaSMNId !== '' && Number(data.analistaSMNId) > 0)
        ? String(data.analistaSMNId)
        : (isAnalista && pessoaId ? String(pessoaId) : String(idAnalista ?? '')),
    }
  }

  const handleFormSubmit = (formData: SuporteFormData) => {
    const dataComDefaults = preencherValoresPadrao(formData)

    if (isEditing && suporteAtual && suporteAtual.id) {
      const idSuporte = suporteAtual.id
      updateMutation.mutate(
        { id: idSuporte, data: { ...dataComDefaults, id: idSuporte } },
        {
          onSuccess: () => {
            onClose()
            reset()
          },
        }
      )
      return
    }

    createMutation.mutate(dataComDefaults, {
      onSuccess: () => {
        onClose()
        reset()
      },
    })
  }

  const footer = (
    <div className="flex justify-end space-x-3 px-6 py-4">
      <button
        onClick={() => {
          onClose()
          reset()
        }}
        className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
        type="button"
      >
        Cancelar
      </button>
      <button
        type="submit"
        form="suporte-form"
        disabled={isSubmitting || createMutation.isLoading || updateMutation.isLoading}
        className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Save className="w-4 h-4 mr-2 inline" />
        {isEditing ? 'Atualizar' : 'Salvar'}
      </button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        reset()
      }}
      title={isEditing ? 'Editar suporte' : 'Novo suporte'}
      subtitle={isEditing ? 'Atualize as informações do suporte selecionado' : 'Preencha os dados para criar um novo suporte'}
      icon={
        <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
          <Headphones className="w-5 h-5" />
        </div>
      }
      size="4xl"
      footer={footer}
    >
      <form className="space-y-3" onSubmit={handleSubmit(handleFormSubmit, (errors) => {
        const mensagensDuplicidade = [
          errors.nome?.message,
          errors.prefClockify?.message
        ].filter(Boolean) as string[]

        if (mensagensDuplicidade.length > 0)
        {
          const mensagem = mensagensDuplicidade.join(' ')
          toast.error('Erro', mensagem)
        }
      })} id="suporte-form">
        <div className="space-y-0.5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5">
            <div className="md:col-span-1">
              <FormField label="Número do Ticket *" error={errors.numeroTicket} labelClassName="block text-sm font-medium text-gray-700 mb-1">
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 text-sm"
                  placeholder="Informe o número do ticket"
                  {...register('numeroTicket', {
                    onChange: (e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
                      e.target.value = numericValue
                    }
                  })}
                  maxLength={6}
                />
              </FormField>
            </div>
            <div className="md:col-span-3">
              <FormField
                label="Nome do Projeto *"
                error={isErroDuplicidade(errors.nome?.message) ? undefined : errors.nome}
                labelClassName="block text-sm font-medium text-gray-700 mb-1"
              >
                <input
                  type="text"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                  placeholder="Informe o nome do projeto"
                  {...register('nome')}
                />
              </FormField>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-10 gap-1.5">
            <div className="md:col-span-4">
              <FormField
                label="Pref. Clockify *"
                error={isErroDuplicidade(errors.prefClockify?.message) ? undefined : errors.prefClockify}
                labelClassName="block text-sm font-medium text-gray-700 mb-1"
              >
                <input
                  type="text"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                  placeholder="Prefixo do Clockify"
                  {...register('prefClockify', {
                    onChange: (e) => {
                      const upper = (e.target.value || '').toUpperCase()
                      e.target.value = upper
                    }
                  })}
                />
              </FormField>
            </div>
            <div className="md:col-span-6">
              <FormField label="Ticket URL" error={errors.ticketUrl} labelClassName="block text-sm font-medium text-gray-700 mb-1">
                <input
                  type="text"
                  readOnly
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                  placeholder="URL do ticket"
                  {...register('ticketUrl')}
                />
              </FormField>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormField label="Status" error={errors.statusCodigo} labelClassName="block text-sm font-medium text-gray-700 mb-1">
                <select
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                  {...register('statusCodigo')}
                >
                  <option value="">Selecione um status</option>
                  {statusFiltrados.map((item: any) => (
                    <option key={item.codigo ?? item.Codigo} value={String(item.codigo ?? item.Codigo)}>
                      {item.descricao ?? item.Descricao}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
            <div>
              <FormField label="Data de Abertura" error={errors.dataAbertura} labelClassName="block text-sm font-medium text-gray-700 mb-1">
                <InputDate
                  register={register('dataAbertura')}
                  error={!!errors.dataAbertura}
                  className="py-1.5 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                />
              </FormField>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormField label="Prioridade" error={errors.prioridadeCodigo} labelClassName="block text-sm font-medium text-gray-700 mb-1">
                <select
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                  {...register('prioridadeCodigo')}
                >
                  <option value="">Selecione uma prioridade</option>
                  {prioridades.map((item: any) => (
                    <option key={item.codigo ?? item.Codigo} value={String(item.codigo ?? item.Codigo)}>
                      {item.descricao ?? item.Descricao}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
            <div>
              <FormField label="Data de Recebimento" error={errors.dataRecebimento} labelClassName="block text-sm font-medium text-gray-700 mb-1">
                <InputDate
                  register={register('dataRecebimento')}
                  error={!!errors.dataRecebimento}
                  className="py-1.5 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                />
              </FormField>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormField label="Solicitante" error={errors.solicitante} labelClassName="block text-sm font-medium text-gray-700 mb-1">
                <div className="relative">
                  <input
                    type="text"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                    placeholder="Selecione ou pesquise o solicitante"
                    value={solicitanteAtual || ''}
                    onChange={(e) => {
                      const valor = e.target.value
                      setValue('solicitante', valor, { shouldDirty: true, shouldValidate: false, shouldTouch: true })
                      if (valor === '')
                        setValue('solicitanteId', undefined, { shouldDirty: true })
                      setSolicitanteSearch(valor)
                      setIsSolicitanteDropdownOpen(true)
                    }}
                    onFocus={() => {
                      setSolicitanteSearch(solicitanteAtual || '')
                      setIsSolicitanteDropdownOpen(true)
                    }}
                    onBlur={() => {
                      // pequeno delay para permitir clique nos itens do dropdown
                      setTimeout(() => {
                        setIsSolicitanteDropdownOpen(false)
                      }, 150)
                    }}
                  />

                  {isSolicitanteDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-theme-sm max-h-60 overflow-auto">
                      <div className="p-2 border-b border-gray-100">
                        <input
                          type="text"
                          className="w-full px-2 py-1 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                          placeholder="Buscar solicitante..."
                          value={solicitanteSearch}
                          onChange={(e) => setSolicitanteSearch(e.target.value)}
                        />
                      </div>

                      {solicitantesFiltrados.length > 0 ? (
                        solicitantesFiltrados.map((pessoa: any) => {
                          const idPessoa = pessoa.idPessoa ?? pessoa.IdPessoa ?? pessoa.id ?? 0
                          const nome = pessoa.nome ?? pessoa.Nome ?? 'Nome não encontrado'

                          return (
                            <button
                              key={idPessoa}
                              type="button"
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleSelectSolicitante(pessoa)}
                            >
                              {nome}
                            </button>
                          )
                        })
                      ) : (
                        <div className="p-3 text-sm text-gray-500 flex items-center justify-between">
                          <span>Nenhum solicitante encontrado.</span>
                          <button
                            type="button"
                            className="px-2 py-1 text-xs font-medium text-white bg-brand-500 rounded-md hover:bg-brand-600"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleOpenSolicitanteModal}
                          >
                            Cadastrar
                          </button>
                        </div>
                      )}

                      {solicitantesFiltrados.length > 0 && (
                        <div className="border-t border-gray-100 p-2">
                          <button
                            type="button"
                            className="w-full text-left text-xs text-brand-600 hover:text-brand-700"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleOpenSolicitanteModal}
                          >
                            + Cadastrar novo solicitante
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </FormField>
            </div>
            <div>
              <FormField label="Analista SMN" error={errors.analistaSMNId} labelClassName="block text-sm font-medium text-gray-700 mb-1">
                <select
                  disabled={isAnalista}
                  className={`w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${isAnalista ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  {...register('analistaSMNId')}
                >
                  <option value="">Selecione um analista</option>
                  {analistasSMN.map((pessoa: any) => (
                    <option key={pessoa.idPessoa ?? pessoa.IdPessoa} value={String(pessoa.idPessoa ?? pessoa.IdPessoa)}>
                      {pessoa.nome ?? pessoa.Nome}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormField label="Desenvolvedor" error={errors.desenvolvedorId} labelClassName="block text-sm font-medium text-gray-700 mb-1">
                <select
                  disabled={isDesenvolvedor}
                  className={`w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${isDesenvolvedor ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  {...register('desenvolvedorId')}
                >
                  <option value="">Selecione um desenvolvedor</option>
                  {desenvolvedores.map((pessoa: any) => (
                    <option key={pessoa.idPessoa ?? pessoa.IdPessoa} value={String(pessoa.idPessoa ?? pessoa.IdPessoa)}>
                      {pessoa.nome ?? pessoa.Nome}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
            <div>
              <FormField label="Data de Conclusão" error={errors.dataConclusao} labelClassName="block text-sm font-medium text-gray-700 mb-1">
                <InputDate
                  register={register('dataConclusao')}
                  error={!!errors.dataConclusao}
                  className="py-1.5 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                />
              </FormField>
            </div>
          </div>
        </div>

        <div className="md:w-1/2 -mt-4">
          <FormField label="Área" error={errors.area} labelClassName="block text-sm font-medium text-gray-700 mb-1">
            <select
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              {...register('area')}
            >
              <option value="">Selecione</option>
              {AREAS_SUPORTE_FIXAS.map((area) => (
                <option key={area} value={area}>
                  {area}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField label="Solução" error={errors.solucao} labelClassName="block text-sm font-medium text-gray-700 mb-1">
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
            rows={4}
            placeholder="Descreva a solução"
            {...register('solucao')}
          />
        </FormField>
      </form>
      <FormularioPessoa
        isOpen={isSolicitanteModalOpen}
        onClose={handleCloseSolicitanteModal}
        pessoa={null}
        isEditing={false}
        cargoFixo="1"
      />
    </Modal>
  )
}
